'use client'

import { useState, useEffect } from 'react'
import { T, detectLang, type Copy, type Lang } from '../landingCopy'

// Trigger button rendered inside the server page (label text comes as
// server-rendered children); owns the modal open state. The modal is
// position:fixed, so rendering it inline here is layout-equivalent to the
// old page-root placement.
export default function InquiryTrigger({ className, children }: { className?: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  const [lang, setLang] = useState<Lang>('DE')

  // Detect browser language on mount — DE for German browsers, EN otherwise.
  useEffect(() => { setLang(detectLang()) }, [])

  return (
    <>
      <button onClick={() => setOpen(true)} className={className}>{children}</button>
      {open && <InquiryModal t={T[lang]} lang={lang} onClose={() => setOpen(false)} />}
    </>
  )
}

// ─── Inquiry modal ────────────────────────────────────────────────

function InquiryModal({ t, lang, onClose }: { t: Copy; lang: Lang; onClose: () => void }) {
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey) }
  }, [onClose])

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 100 }}>
      <div onClick={e => e.stopPropagation()} className="fx-card" style={{ width: '100%', maxWidth: 520, maxHeight: 'calc(100dvh - 2rem)', overflow: 'auto', padding: '1.75rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem', gap: '1rem' }}>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600, margin: 0, letterSpacing: '-0.01em' }}>{t.inquireTitle}</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '1.5rem', color: 'var(--color-text-muted)', padding: 0, lineHeight: 1, marginTop: '-0.25rem' }} aria-label="Close">×</button>
        </div>
        <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', margin: '0 0 1.25rem', lineHeight: 1.5 }}>{t.inquireIntro}</p>
        <SurveyForm t={t} lang={lang} />
      </div>
    </div>
  )
}

// ─── Survey — typewriter ───────────────────────────────────────────

function SurveyForm({ t, lang }: { t: Copy; lang: Lang }) {
  const [step, setStep] = useState(0)
  const [answers, setAnswers] = useState<Record<string, any>>({})
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle')
  const total = t.survey.q.length
  const current = t.survey.q[step]
  const progress = Math.round((step / total) * 100)

  const [typed, setTyped] = useState('')
  const [done, setDone] = useState(false)
  useEffect(() => {
    setTyped(''); setDone(false)
    let cancelled = false
    const full = current.title
    let i = 0
    const tick = () => {
      if (cancelled) return
      i++
      setTyped(full.slice(0, i))
      if (i < full.length) setTimeout(tick, 22 + Math.random() * 20)
      else setDone(true)
    }
    const kick = setTimeout(tick, 120)
    return () => { cancelled = true; clearTimeout(kick) }
  }, [step, lang, current.title])

  const setVal = (key: string, val: any) => setAnswers(prev => ({ ...prev, [key]: val }))

  const canProceed = () => {
    if (!current.required) return true
    const v = answers[current.key]
    if (current.type === 'email') return typeof v === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
    return v != null && String(v).trim().length > 0
  }

  const submit = async () => {
    setStatus('sending')
    try {
      const res = await fetch('/api/leads', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: answers.name || null,
          email: answers.email,
          role: answers.role || null,
          goals: answers.goals || null,
          team_size: answers.team_size ? Number(answers.team_size) : null,
          asset_classes: answers.asset_classes || null,
          tax_regions: answers.tax_regions || null,
          callback: !!answers.callback?.yes,
          callback_window: answers.callback?.when || null,
          language: lang,
          survey: answers,
        }),
      })
      setStatus(res.ok ? 'success' : 'error')
    } catch { setStatus('error') }
  }

  if (status === 'success') {
    return <p style={{ fontSize: '0.9375rem', margin: '1rem 0', color: 'var(--color-text-primary)' }}>{t.survey.success}</p>
  }

  const isLast = step === total - 1

  return (
    <div>
      <div style={{ height: 2, background: 'var(--color-border)', borderRadius: 2, overflow: 'hidden', marginBottom: '1.25rem' }}>
        <div style={{ width: `${progress}%`, height: '100%', background: 'var(--color-accent)', transition: 'width 0.25s ease' }} />
      </div>

      <h3 style={{ fontSize: '1.0625rem', fontWeight: 600, margin: '0 0 0.5rem', minHeight: '1.5em', lineHeight: 1.3 }}>
        {typed}
        {!done && <span className="fx-caret" />}
        {done && !current.required && <span style={{ fontSize: '0.6875rem', fontWeight: 400, color: 'var(--color-text-muted)', marginLeft: '0.625rem', textTransform: 'uppercase', letterSpacing: '0.08em' }}>optional</span>}
      </h3>
      {done && current.hint && <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', lineHeight: 1.5, margin: '0 0 1rem' }}>{current.hint}</p>}

      <div style={{ opacity: done ? 1 : 0, transition: 'opacity 0.2s ease 0.05s', pointerEvents: done ? 'auto' : 'none' }}>
        {current.type === 'single' && current.options && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {current.options.map(opt => {
              const selected = answers[current.key] === opt
              return (
                <button key={opt} onClick={() => setVal(current.key, opt)} style={{ textAlign: 'left', padding: '0.75rem 0.875rem', borderRadius: 6, border: `1px solid ${selected ? 'var(--color-accent)' : 'var(--color-border)'}`, background: selected ? 'var(--color-accent-bg)' : 'transparent', color: 'var(--color-text-primary)', fontSize: '0.9375rem', cursor: 'pointer' }}>{opt}</button>
              )
            })}
          </div>
        )}

        {current.type === 'multi' && current.options && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem' }}>
            {current.options.map(opt => {
              const arr: string[] = Array.isArray(answers[current.key]) ? answers[current.key] : []
              const selected = arr.includes(opt)
              return (
                <button key={opt} onClick={() => setVal(current.key, selected ? arr.filter(x => x !== opt) : [...arr, opt])} style={{ padding: '0.45rem 0.75rem', borderRadius: 6, border: `1px solid ${selected ? 'var(--color-accent)' : 'var(--color-border)'}`, background: selected ? 'var(--color-accent-bg)' : 'transparent', color: 'var(--color-text-primary)', fontSize: '0.8125rem', cursor: 'pointer' }}>{selected && <span style={{ marginRight: 4 }}>✓</span>}{opt}</button>
              )
            })}
          </div>
        )}

        {current.type === 'text' && (
          <input type="text" value={answers[current.key] || ''} onChange={e => setVal(current.key, e.target.value)} placeholder={current.placeholder} style={inputCss} />
        )}

        {current.type === 'textarea' && (
          <textarea value={answers[current.key] || ''} onChange={e => setVal(current.key, e.target.value)} placeholder={current.placeholder} rows={3} style={{ ...inputCss, resize: 'vertical', fontFamily: 'inherit' }} />
        )}

        {current.type === 'number' && (
          <input type="number" value={answers[current.key] ?? ''} onChange={e => setVal(current.key, e.target.value ? Number(e.target.value) : '')} placeholder={current.placeholder} min={0} style={{ ...inputCss, width: 140 }} />
        )}

        {current.type === 'email' && (
          <input type="email" value={answers[current.key] || ''} onChange={e => setVal(current.key, e.target.value)} placeholder={current.placeholder} required style={inputCss} />
        )}

        {current.type === 'callback' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', gap: '0.375rem' }}>
              {[
                { key: 'yes', label: lang === 'DE' ? 'Ja, gerne' : 'Yes, please' },
                { key: 'no', label: lang === 'DE' ? 'Nein, E-Mail genügt' : 'No, email is fine' },
              ].map(o => {
                const selected = answers[current.key]?.yes === (o.key === 'yes')
                return (
                  <button key={o.key} onClick={() => setVal(current.key, { yes: o.key === 'yes', when: answers[current.key]?.when || '' })} style={{ flex: 1, padding: '0.7rem', borderRadius: 6, border: `1px solid ${selected ? 'var(--color-accent)' : 'var(--color-border)'}`, background: selected ? 'var(--color-accent-bg)' : 'transparent', color: 'var(--color-text-primary)', fontSize: '0.9375rem', cursor: 'pointer' }}>{o.label}</button>
                )
              })}
            </div>
            {answers[current.key]?.yes && (
              <input type="text" value={answers[current.key]?.when || ''} onChange={e => setVal(current.key, { yes: true, when: e.target.value })} placeholder={lang === 'DE' ? 'z. B. Di Vormittag, oder +41 79 …' : 'e.g. Tuesday morning, or +41 79 …'} style={inputCss} />
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem', gap: '0.5rem' }}>
        <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} style={{ padding: '0.55rem 0.75rem', background: 'transparent', border: 'none', cursor: step === 0 ? 'default' : 'pointer', color: 'var(--color-text-muted)', fontSize: '0.875rem', opacity: step === 0 ? 0 : 1, pointerEvents: step === 0 ? 'none' : 'auto' }}>{t.survey.back}</button>
        <div style={{ display: 'flex', gap: '0.375rem' }}>
          {!current.required && (
            <button onClick={() => { if (isLast) submit(); else setStep(s => s + 1) }} style={{ padding: '0.55rem 0.9rem', background: 'transparent', border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer', color: 'var(--color-text-secondary)', fontSize: '0.875rem' }}>{t.survey.skip}</button>
          )}
          <button onClick={() => { if (!canProceed()) return; if (isLast) submit(); else setStep(s => s + 1) }} disabled={!canProceed() || status === 'sending'} style={{ padding: '0.55rem 1rem', background: 'var(--color-accent)', color: '#fff', border: 'none', borderRadius: 6, cursor: (!canProceed() || status === 'sending') ? 'default' : 'pointer', fontSize: '0.875rem', fontWeight: 500, opacity: !canProceed() || status === 'sending' ? 0.5 : 1 }}>
            {status === 'sending' ? t.survey.sending : isLast ? t.survey.submit : t.survey.next}
          </button>
        </div>
      </div>

      {status === 'error' && (
        <div style={{ marginTop: '0.875rem', padding: '0.6rem 0.8rem', borderRadius: 6, background: 'rgba(220,38,38,0.07)', border: '1px solid var(--color-loss)', color: 'var(--color-loss)', fontSize: '0.8125rem' }}>{t.survey.errorRetry}</div>
      )}
    </div>
  )
}

const inputCss: React.CSSProperties = {
  width: '100%', padding: '0.75rem 0.875rem', fontSize: '0.9375rem',
  borderRadius: 6, border: '1px solid var(--color-border)',
  background: 'transparent', color: 'var(--color-text-primary)',
  outline: 'none', boxSizing: 'border-box',
}
